import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Box, Skeleton } from '@mui/material';
import {
  Dashboard as OverviewIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  ShowChart as ShowChartIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMobileHeader } from '../../../context/MobileHeaderContext';
import { usePourNav } from '../../../context/PourNavContext';
import type { DashboardResponse, Pour, Pour2Limit } from '../../../services/pours';
import { pourComputesMaturity } from '../../../utils/calibrationCheck';
import { formatAgo, formatLocalClock } from '../../../utils/relativeTime';
import { useNow } from '../../../utils/useNow';
import { MOBILE_MAIN_PX } from '../../mobile/mobileChrome';
import MobileToast from '../../mobile/MobileToast';
import PourActionsMenu from '../../mobile/PourActionsMenu';
import { CalibrationNotice } from './CalibrationNotice';
import { MobileChartsView } from './MobileChartsView';
import type { MobileChartKey } from './mobileDashboardModel';
import { MobileLocationView } from './MobileLocationView';
import { MobileOverview } from './MobileOverview';
import { useDashboardModel } from './useDashboardModel';

/** The phone dashboard's three tabs (the bottom bar). */
export type MobileTab = 'overview' | 'location' | 'charts';

const TABS: MobileTab[] = ['overview', 'location', 'charts'];

export interface MobilePourDashboardProps {
  pourId: string;
  /** The dashboard reply; null while the first load runs. */
  data: DashboardResponse | null;
  /** The Control Panel row the pour was opened from (router state): the
   *  header and status strip paint from it while the first load runs. */
  listPour?: Pour | null;
  pourLimits: Pour2Limit[];
  /** When the data on screen was fetched: freshness is judged against it,
   *  not the wall clock. */
  lastRefresh: Date;
  /** A refresh is in flight. */
  refreshing: boolean;
  /** The last refresh failed (the data on screen is older). */
  refreshFailedAt: Date | null;
  onRefresh: () => void;
  /** Bumped after each successful poll or refresh: charts reload quietly. */
  refreshTick: number;
  tab: MobileTab;
  onTabChange: (tab: MobileTab) => void;
  chartKey: MobileChartKey | null;
  onChartKeyChange: (key: MobileChartKey) => void;
  selectedLocationId?: string;
  /** Selects a location (and opens the Location tab). */
  onSelectLocation: (locationId: string) => void;
  /** The pour was stopped here: reload. */
  onStopped: () => void;
}

/** Back to where the user came from, else the Control Panel. */
const useGoBack = (): (() => void) => {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate('/', { replace: true });
  }, [navigate]);
};

/** The top of the page on a tab switch (jsdom has no scrolling). */
const scrollToTop = () => {
  try {
    window.scrollTo(0, 0);
  } catch {
    // Not supported (tests).
  }
};

/**
 * The Pour Dashboard below 900px: the header in the shell's AppBar (back,
 * pour name, project and data age, refresh, the '...' menu), three bottom
 * tabs (Overview with the outside-limits count, Location, Charts), the
 * calibration notice and, after a failed refresh, how old the data is.
 * Desktop never renders it.
 */
export const MobilePourDashboard: React.FC<MobilePourDashboardProps> = ({
  pourId,
  data,
  listPour,
  pourLimits,
  lastRefresh,
  refreshing,
  refreshFailedAt,
  onRefresh,
  refreshTick,
  tab,
  onTabChange,
  chartKey,
  onChartKeyChange,
  selectedLocationId,
  onSelectLocation,
  onStopped,
}) => {
  const { t } = useTranslation();
  const now = useNow();
  const goBack = useGoBack();
  const { setNav } = usePourNav();
  const refNow = lastRefresh.getTime();
  const model = useDashboardModel(data, pourLimits, refNow);
  const pour = data?.pour ?? null;

  // ---- Bottom tabs --------------------------------------------------------
  const outside = model ? model.counts.outsideLimits : listPour?.OverLimitCount ?? 0;
  const onTabChangeRef = useRef(onTabChange);
  onTabChangeRef.current = onTabChange;
  useEffect(() => {
    setNav({
      items: [
        {
          key: 'overview',
          label: t('mobile.dashboard.tabs.overview', { defaultValue: 'Overview' }),
          icon: (
            <Badge badgeContent={outside} color="error" max={99} data-testid="overview-badge">
              <OverviewIcon />
            </Badge>
          ),
        },
        { key: 'location', label: t('mobile.dashboard.tabs.location', { defaultValue: 'Location' }), icon: <LocationOnIcon /> },
        { key: 'charts', label: t('mobile.dashboard.tabs.charts', { defaultValue: 'Charts' }), icon: <ShowChartIcon /> },
      ],
      active: TABS.indexOf(tab),
      onSelect: (index: number) => {
        const next = TABS[index];
        if (next) onTabChangeRef.current(next);
        scrollToTop();
      },
    });
  }, [setNav, tab, outside, t]);
  // The page's own bar comes back when the dashboard closes.
  useEffect(() => () => setNav(null), [setNav]);

  // ---- Toast --------------------------------------------------------------
  const [toast, setToast] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const onStoppedRef = useRef(onStopped);
  onStoppedRef.current = onStopped;
  const handleStopped = useCallback(() => onStoppedRef.current(), []);
  const handleMessage = useCallback((text: string, severity: 'success' | 'error') => setToast({ text, severity }), []);

  // ---- Header -------------------------------------------------------------
  const title = pour
    ? pour.name || pour.description || ''
    : listPour?.PourName || t('mobile.dashboard.header.loading', { defaultValue: 'Loading pour...' });
  const project = pour ? pour.project_name || '' : listPour?.ProjectName || '';
  const pourCode = (pour?.pour_code || listPour?.PourCode || '').trim();
  const failed = !!refreshFailedAt && !!data;
  // When the data was fetched, in the phone's own time (as the Control
  // Panel says it), not the pour's.
  const dataTime = formatLocalClock(refNow, now);
  let status: string;
  if (refreshing || !data) status = t('mobile.dashboard.header.updating', { defaultValue: 'Updating' });
  // The time first: the one-line subtitle cuts its end on a narrow phone.
  else if (failed) status = t('mobile.dashboard.header.failed', { defaultValue: 'Data from {{time}} · could not refresh', time: dataTime });
  else status = t('mobile.dashboard.header.updated', { defaultValue: 'Updated {{ago}}', ago: formatAgo(refNow, now, t) });
  // Under the name: the Pour ID and the project (the Control Panel's card).
  // The data's age shows only while it is loading or a refresh failed; the
  // Overview, Location and Charts each say how new their readings are.
  const idLine = [pourCode, project].filter(Boolean).join(' · ');
  const subtitleText = failed ? `${idLine}\n${status}` : !data || refreshing ? [idLine, status].filter(Boolean).join(' · ') : idLine;
  const inProgress = pour ? pour.status === '1' : false;
  const hasReadings = !!model && (model.phase.newestReadingAt !== null || model.locations.some((l) => !!l.last_read));
  const menuPour = { PourID: pourId, PourCode: listPour?.PourCode ?? null, PourName: title };

  useMobileHeader(
    {
      back: { label: t('mobile.dashboard.header.back', { defaultValue: 'Back' }), onClick: goBack },
      title,
      wrapTitle: true,
      subtitle: (
        <>
          {idLine && (
            // The project shortens first; the Pour ID always shows in full.
            <Box component="span" data-testid="header-id-line" sx={{ display: 'flex', minWidth: 0 }}>
              {pourCode && <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'pre' }}>{project ? `${pourCode} · ` : pourCode}</Box>}
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {project}
              </Box>
              {(!data || refreshing) && !failed && <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'pre' }}>{` · ${status}`}</Box>}
            </Box>
          )}
          {failed ? (
            <Box component="span" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600 }}>
              <WarningAmberIcon aria-hidden sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5 }} />
              {status}
            </Box>
          ) : !idLine && (!data || refreshing) ? (
            status
          ) : null}
        </>
      ),
      actions: [
        {
          key: 'refresh',
          label: t('mobile.dashboard.header.refresh', { defaultValue: 'Refresh' }),
          icon: <RefreshIcon />,
          onClick: onRefresh,
          busy: refreshing,
        },
      ],
      trailing: data ? (
        <PourActionsMenu
          pour={menuPour}
          inProgress={inProgress}
          hasReadings={hasReadings}
          onStopped={handleStopped}
          onMessage={handleMessage}
          sx={{ color: 'inherit', width: 48, height: 48 }}
        />
      ) : null,
    },
    // Back and refresh always call the latest handlers (useMobileHeader).
    [title, subtitleText, failed, refreshing, !!data, pourId, menuPour.PourCode, inProgress, hasReadings, t],
  );

  // A card opens its location on the Location tab, from the top.
  const openFromOverview = useCallback(
    (locationId: string) => {
      onSelectLocation(locationId);
      scrollToTop();
    },
    [onSelectLocation],
  );

  // ---- Body ---------------------------------------------------------------
  let body: React.ReactNode;
  if (tab === 'overview') {
    body = (
      <MobileOverview
        data={data}
        listPour={listPour}
        pourLimits={pourLimits}
        refNow={refNow}
        onSelectLocation={openFromOverview}
      />
    );
  } else if (!data) {
    body = (
      <Box aria-busy="true" data-testid="mobile-tab-skeleton">
        <Skeleton variant="rounded" height={48} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={160} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={260} />
      </Box>
    );
  } else if (tab === 'location') {
    body = (
      <MobileLocationView
        pourId={pourId}
        data={data}
        pourLimits={pourLimits}
        refNow={refNow}
        refreshTick={refreshTick}
        selectedLocationId={selectedLocationId}
        onSelectLocation={onSelectLocation}
      />
    );
  } else {
    body = (
      <MobileChartsView
        pourId={pourId}
        data={data}
        pourLimits={pourLimits}
        refNow={refNow}
        refreshTick={refreshTick}
        chartKey={chartKey}
        onChartKeyChange={onChartKeyChange}
      />
    );
  }

  return (
    <Box
      data-testid="mobile-pour-dashboard"
      sx={{ mx: -MOBILE_MAIN_PX, px: 1.5, minWidth: 0, overflowX: 'hidden' }}
    >
      {pour && (
        <CalibrationNotice
          check={pour.calibration_check}
          computesMaturity={pourComputesMaturity(pour.monitoring_functions, pour.functionality)}
          overview={tab === 'overview'}
        />
      )}
      {body}
      <MobileToast
        open={!!toast}
        message={toast?.text ?? ''}
        severity={toast?.severity ?? 'success'}
        onClose={() => setToast(null)}
      />
    </Box>
  );
};

export default MobilePourDashboard;
